import React from 'react';
import { colors } from '../../../style';
import { FadeLoader } from 'react-spinners';
import dayjs from 'dayjs';
import { Dialog } from '../../base/simple-styled-components';
import { Text, DocumentLink } from '../../base/tw-components';
import { getDocument } from '../../../utils/web-apis-client';
import useAsyncHandler from '../../../hooks/use-async-handler';
import { ListViewItemProps } from '@progress/kendo-react-listview';
import { DocumentProps } from '..';
import { UploadStatusLabel } from '../upload-status-label';

export const MAX_DOWNLOAD_RETRIES = 45;
export const DOWNLOAD_RETRY_INTERVAL_MS = 2000;

export const generateDownloadHandler = (item: any) => {
  return async () => getDocument(item.caseId, item.documentKey);
};

type Props = {
  children: React.ReactNode;
  id?: string;
};

const ListContainer = ({ children, id }: Props) => (
  <div
    id={id}
    className="tw-flex tw-flex-col md:tw-flex-row tw-border-b tw-border-solid tw-border-unm-border1 tw-border-0 tw-py-5 tw-pb-2 last:tw-border-b-0"
  >
    {children}
  </div>
);

export interface IItemRenderProps extends ListViewItemProps {
  openModal: (s3Url: string) => void;
}

/**
 * MyItemRender - Renders custom items in the ListView component
 * @param props
 */
const ItemRender: (props: IItemRenderProps) => React.ReactElement = (props) => {
  const item = props.dataItem as DocumentProps;
  const index = props.index;
  const [displayLoading, setDisplayLoading] = React.useState(false);
  const {
    data: s3Url,
    error: showError,
    setError: setShowError,
    isBusy: downloading,
    startRequest,
    reset,
  } = useAsyncHandler(
    generateDownloadHandler(item),
    undefined,
    undefined,
    (r) => r !== 'pending',
    '',
    MAX_DOWNLOAD_RETRIES,
    DOWNLOAD_RETRY_INTERVAL_MS,
  );

  const openDocument = React.useCallback(() => {
    setShowError(false);
    setDisplayLoading(true);
    startRequest();
  }, [setShowError, setDisplayLoading, startRequest]);

  const { openModal } = props;
  React.useEffect(() => {
    if (s3Url.length > 0 && s3Url !== 'pending') {
      openModal(s3Url);
      setDisplayLoading(false);
      // consume the url so re-renders (e.g. paging) don't reopen the modal
      reset();
    }
  }, [openModal, s3Url, reset]);

  React.useEffect(() => {
    if (showError) {
      setDisplayLoading(false);
      setTimeout(() => {
        setShowError(false);
        reset();
      }, 7000);
    }
  }, [setShowError, setDisplayLoading, reset, showError]);

  return (
    <>
      {showError && (
        <Dialog
          id={`document-error-modal`}
          onClose={() => setShowError(false)}
          title={'Loading Error'}
        >
          {downloading && (
            <div className="tw-flex tw-justify-center tw-mt-6 tw-mb-0">
              <FadeLoader
                height={14}
                width={5}
                radius={2}
                margin={0}
                color={colors.blue}
              />
            </div>
          )}
          <Text fontSize="base" className=" tw-p-4 tw-pb-5">
            Sorry, there was a problem retrieving {item.name} from{' '}
            {dayjs(item.receivedDate).format('MM/DD/YY')}.
          </Text>
        </Dialog>
      )}

      {displayLoading && (
        <Dialog
          id={`document-loading-modal`}
          onClose={() => setDisplayLoading(false)}
          title={'Retrieving document'}
        >
          {downloading && (
            <div className="tw-flex tw-justify-center tw-mt-6 tw-mb-0">
              <FadeLoader
                height={14}
                width={5}
                radius={2}
                margin={0}
                color={colors.blue}
              />
            </div>
          )}
          <Text fontSize="base" align="center" className="tw-p-4 tw-pb-5">
            Retrieving {item.name} for you. This could take up to a minute.
          </Text>
        </Dialog>
      )}

      <ListContainer id={`document-list-container-${index}`}>
        {/* StartDate */}
        <Text
          id={`document-date-${index}`}
          fontSize="base"
          className="tw-hidden md:tw-block md:tw-w-1/4"
        >
          {dayjs(item.receivedDate).format('MMM D, YYYY')}
        </Text>

        {/* Title */}
        <div
          className="tw-w-full md:tw-w-5/12"
          data-gtm-dl={JSON.stringify({
            attribute1: 'documents',
            attribute2: 'undefined',
            attribute3: 'download_file',
            attribute4: item?.name ?? 'undefined',
          })}
        >
          {item.pending && (
            <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
              <Text id={`document-pending-name-${index}`} fontSize="base">
                {item.name}
              </Text>
              <UploadStatusLabel
                id={`document-upload-status-${index}`}
                status={item.uploadStatus}
              />
            </div>
          )}
          {!item.pending && (
            <DocumentLink
              id={`document-link-${index}`}
              onClick={openDocument}
              disabled={downloading}
            >
              {item.name} <span className="k-icon k-i-download tw-ml-2"></span>
            </DocumentLink>
          )}
        </div>

        {/* ClaimID */}
        <div className="tw-p-0 tw-flex tw-flex-row md:tw-w-1/3">
          <Text
            id={`document-claimid-${index}`}
            fontSize="base"
            className="tw-w-2/3 md:tw-w-full"
          >
            {item.caseId}
          </Text>

          <Text
            id={`document-date-mobile-${index}`}
            fontSize="base"
            className="tw-w-1/3 md:tw-hidden tw-text-right sm:tw-text-left"
          >
            {dayjs(item.receivedDate).format('MM/DD/YY')}
          </Text>
        </div>
      </ListContainer>
    </>
  );
};

export default ItemRender;
